import { uniq } from 'es-toolkit';

import { positiveQuantityOrNull } from '@paragon/client-tools';
import { AssemblyGroup, ComponentDesignUploadFile, Line, Station } from '@paragon/contract-library/lib/designService';
import { designService } from '@paragon/data-connections';
import { enqueueError, enqueueSuccess, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { forceArray } from '@paragon/utilities/lib/forceArray';

import { getHolisticViewModel, LineViewModel } from '../selectors/holisticViewModelSelector';
import { assemblyGroupsSlice } from '../store/assemblyGroupsSlice';
import { uploadComponentDesigns } from './componentDesignActions';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActions';

export type AssemblyGroupStarterData = Pick<AssemblyGroup, 'name' | 'notes' | 'componentDesigns'>;
export function createAssemblyGroups(
  assemblyGroupStarterDataList: readonly AssemblyGroupStarterData[],
): AsyncThunkActionReturn<boolean> {
  return async function createAssemblyGroupThunkAction(dispatch) {
    const assemblyGroupWithPlurality = assemblyGroupStarterDataList.length === 1 ? 'Assembly Group' : 'Assembly Groups';
    const spinnerMessage = `Creating ${assemblyGroupWithPlurality}...`;
    const spinnerId = dispatch(addSpinnerMessage(spinnerMessage));

    const results = await Promise.all(
      assemblyGroupStarterDataList.map((assemblyGroupStarterData) =>
        designService.assemblyGroups
          .createAssemblyGroup(assemblyGroupStarterData)
          .then((createdAssemblyGroup) => {
            dispatch(assemblyGroupsSlice.actions.loadedAssemblyGroup(createdAssemblyGroup));
            return true;
          })
          .catch((error) => {
            console.error(error);
            dispatch(enqueueError(`Unable to create Assembly Group ${assemblyGroupStarterData.name}.`));
            return false;
          }),
      ),
    );

    const allSucceeded = results.every((success) => success);
    if (allSucceeded) dispatch(enqueueSuccess(`${assemblyGroupWithPlurality} created`));
    dispatch(removeSpinnerMessage(spinnerId));
    return allSucceeded;
  };
}

export function editAssemblyGroup(
  assemblyGroupGuid: string,
  updates: Partial<Pick<AssemblyGroup, 'name' | 'notes' | 'archived'>>,
): AsyncThunkActionVoid {
  return async function editAssemblyGroupThunkAction(dispatch, getState) {
    const existingAssemblyGroup = getState().assemblyGroups.data[assemblyGroupGuid];
    const spinnerMessage = `Updating ${existingAssemblyGroup.name}...`;
    const spinnerId = dispatch(addSpinnerMessage(spinnerMessage));

    try {
      const updatedAssemblyGroup = await designService.assemblyGroups.updateAssemblyGroup(assemblyGroupGuid, {
        name: updates.name ?? existingAssemblyGroup.name,
        notes: updates.notes ?? existingAssemblyGroup.notes,
        archived: updates.archived ?? existingAssemblyGroup.archived,
      });
      if (updatedAssemblyGroup.archived) {
        dispatch(assemblyGroupsSlice.actions.singleAssemblyGroupArchived(updatedAssemblyGroup.guid));
      } else {
        dispatch(assemblyGroupsSlice.actions.singleAssemblyGroupEdited(updatedAssemblyGroup));
      }
    } catch (error) {
      console.log(error);
      dispatch(enqueueError(`Unable to update ${existingAssemblyGroup.name}.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function addComponentDesignsToAssemblyGroup(
  assemblyGroupGuid: string,
  componentDesignFiles: ComponentDesignUploadFile[],
): AsyncThunkActionVoid {
  return async function addComponentDesignsToAssemblyGroupThunkAction(dispatch, getState) {
    const existingAssemblyGroup = getState().assemblyGroups.data[assemblyGroupGuid];
    const spinnerId = dispatch(addSpinnerMessage(`Adding component designs to ${existingAssemblyGroup.name}...`));

    try {
      const uploadFilesResult = await dispatch(uploadComponentDesigns(componentDesignFiles));
      const newComponentDesigns = Object.fromEntries(
        uploadFilesResult?.componentDesignsWithTimestamps.map(({ componentDesign }) => [
          componentDesign.guid,
          {
            index: null,
            quantity: positiveQuantityOrNull(uploadFilesResult.quantitiesFromFiles[componentDesign.guid]),
          },
        ]) || [],
      );

      if (Object.keys(newComponentDesigns).length === 0) {
        dispatch(enqueueWarning('No component designs were able to be created from the files.'));
        return;
      }

      const updatedAssemblyGroup = await designService.assemblyGroups.putComponentDesigns(assemblyGroupGuid, {
        ...existingAssemblyGroup.componentDesigns,
        ...newComponentDesigns,
      });
      dispatch(assemblyGroupsSlice.actions.loadedAssemblyGroup(updatedAssemblyGroup));

      dispatch(enqueueSuccess(`Component designs added to ${existingAssemblyGroup.name}.`));
    } catch (error) {
      console.log(error);
      dispatch(enqueueError(`Unable to add component designs to ${existingAssemblyGroup.name}.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function updateComponentDesignQuantityInAssemblyGroup(
  assemblyGroupGuid: string,
  componentDesignGuid: string,
  quantity: number,
): AsyncThunkActionVoid {
  return async function updateComponentDesignQuantityThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage(`Updating component design quantity...`));

    try {
      const existingAssemblyGroup = getState().assemblyGroups.data[assemblyGroupGuid];
      const updatedAssemblyGroup = await designService.assemblyGroups.putComponentDesigns(assemblyGroupGuid, {
        ...existingAssemblyGroup.componentDesigns,
        [componentDesignGuid]: { ...existingAssemblyGroup.componentDesigns[componentDesignGuid], quantity },
      });
      dispatch(assemblyGroupsSlice.actions.loadedAssemblyGroup(updatedAssemblyGroup));
    } catch (error) {
      console.log(error);
      dispatch(enqueueError(`Unable to update component design quantity.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function removeComponentDesignsFromAssemblyGroup(
  assemblyGroupGuid: string,
  componentDesignGuids: string | string[],
): AsyncThunkActionVoid {
  return async function removeComponentDesignsFromAssemblyGroupThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage(`Removing component designs...`));
    const componentDesignGuidsArray = forceArray(componentDesignGuids);
    const existingAssemblyGroup = getState().assemblyGroups.data[assemblyGroupGuid];

    try {
      const updatedAssemblyGroup = await designService.assemblyGroups.putComponentDesigns(
        assemblyGroupGuid,
        Object.fromEntries(
          Object.entries(existingAssemblyGroup.componentDesigns).filter(
            ([guid]) => !componentDesignGuidsArray.includes(guid),
          ),
        ),
      );
      dispatch(assemblyGroupsSlice.actions.loadedAssemblyGroup(updatedAssemblyGroup));
    } catch (error) {
      console.log(error);
      dispatch(enqueueError(`Unable to remove component designs from ${existingAssemblyGroup.name}.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

function sendAssemblyGroupToStation(
  assemblyGroupGuid: string,
  stationGuid: string,
  clearExistingComponents: boolean,
): AsyncThunkActionVoid {
  return async function sendAssemblyGroupToStationThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage(`Sending assembly group to station...`));

    try {
      await designService.assemblyGroups.sendToStation(assemblyGroupGuid, stationGuid, clearExistingComponents);
    } catch (error) {
      console.log(error);
      dispatch(enqueueError(`Unable to send assembly group to station.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function sendAssemblyGroupToStations(
  assemblyGroupGuid: string,
  stations: Station[],
  clearExistingComponents: boolean,
): AsyncThunkActionVoid {
  return async function sendAssemblyGroupToStationsThunkAction(dispatch) {
    const plurality = stations.length === 1 ? '' : 's';
    const spinnerId = dispatch(addSpinnerMessage(`Sending assembly group to station${plurality}...`));

    try {
      await Promise.all(
        stations.map((station) =>
          dispatch(sendAssemblyGroupToStation(assemblyGroupGuid, station.guid, clearExistingComponents)),
        ),
      );
      dispatch(enqueueSuccess(`Assembly group sent to station${plurality} successfully.`));
    } catch (error) {
      console.log(error);
      dispatch(enqueueError(`Unable to send assembly group to stations.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function sendAssemblyGroupToLines(
  assemblyGroupGuid: string,
  lines: Line[],
  clearExistingComponents: boolean,
): AsyncThunkActionVoid {
  return async function sendAssemblyGroupToLinesThunkAction(dispatch, getState) {
    const plurality = lines.length === 1 ? '' : 's';
    const spinnerId = dispatch(addSpinnerMessage(`Sending assembly group to line${plurality}...`));

    const holisticViewModel = getHolisticViewModel(getState());
    if (holisticViewModel === 'loading' || holisticViewModel === 'error') {
      dispatch(enqueueError(`Unable to send assembly group to lines - wait for them to finish loading or refresh.`));
      return;
    }
    const stationsForLines = uniq(
      lines.flatMap(
        (line) =>
          Object.values((holisticViewModel.flatView[line.guid] as LineViewModel)?.children.stations).map(
            (station) => station.source.guid,
          ) || [],
      ),
    );

    try {
      await Promise.all(
        stationsForLines.map((stationGuid) =>
          dispatch(sendAssemblyGroupToStation(assemblyGroupGuid, stationGuid, clearExistingComponents)),
        ),
      );
      dispatch(enqueueSuccess(`Assembly group sent to line${plurality} successfully.`));
    } catch (error) {
      console.log(error);
      dispatch(enqueueError(`Unable to send assembly group to lines.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}
